import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './home/home.component';
import { PromptLogComponent } from './prompt-log/prompt-log.component';
import { AdvisorComponent } from './advisor/advisor.component';
import { AdvisorNewComponent } from './advisor_new/advisor-new.component';
import { CollectionsComponent } from './collections/collections.component';
import { AdminComponent } from './admin/admin.component';
import { ReviewersComponent } from './reviewers/reviewers.component';
import { ReviewersDetailsComponent } from './reviewers/reviewers-details/reviewers.details.component';
import { SampleStylesComponent } from './sample-styles/sample-styles.component';
import { LibrariesComponent } from './Libraries/libraries.component';
import { WorkspaceListComponent } from './workspaces/components/workspace-list/workspace-list.component';
import { WorkspaceTabComponent } from './workspaces/components/workspace-tab/workspace-tab.component';
import { CreateCommonFormComponent } from './workspaces/components/common-form/create-common-form/create-common-form.component';
import { WorkspaceMemberGuard } from './workspaces/utils/guards/workspace-member.guard';
import { TestOpenerComponent } from './workspaces/components/test-opener/test-opener.component';
import { EditDocumentComponent } from './workspaces/components/edit-document/edit-document.component';
import { WorkspaceActivatedGuard } from './workspaces/utils/guards/workspace-activated.guard';
import { WorkspaceRoleGuard } from './workspaces/utils/guards/workspace-role.guard';
import { WorkspaceRole } from './workspaces/models/users';
import { TaskViewComponent } from './workspaces/components/task-view/task-view.component';
import { TaskKanbanComponent } from './workspaces/components/task-kanban/task-kanban.component';
import { TaskListComponent } from './workspaces/components/task-list/task-list.component';
import { TaskDetailsPageRouteComponent } from './workspaces/components/task-details/page/task-details-page-route.component';
import { TaskDetailsDialogRouteComponent } from './workspaces/components/task-details/dialog/task-details-dialog-route.component';
import { EditHomeComponent } from '@workspaces/components/edit-home/edit-home.component';
import { WorkspaceDeletedGuard } from '@workspaces/utils/guards/workspace-deleted.guard';
import { MetadataTitleResolver, RouteMetadata } from '@workspaces/utils/route-meta.resolver';
import { TaskTimelineComponent } from '@workspaces/components/task-timeline/task-timeline.component';
import { VersionHistoryComponent } from '@workspaces/components/version-history/version-history.component';
import { environment } from 'src/environments/environment';
import { TemplateListComponent } from './workspaces/components/template-list/template-list.component';
import { TemplateCreateComponent } from '@workspaces/components/template-create/template-create.component';
import { TemplateEditComponent } from '@workspaces/components/template-edit/template-edit.component';
import { WorkspaceAcceptComponent } from '@workspaces/components/workspace-accept/workspace-accept.component';

const routes: Routes = [
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  { path: 'home', component: HomeComponent },
  { path: 'playground', redirectTo: 'home' },
  { path: 'advisor', component: AdvisorNewComponent },
  { path: 'advisor-old', component: AdvisorComponent },
  { path: 'collections', component: CollectionsComponent },
  { path: 'prompt-log', component: PromptLogComponent },
  { path: 'admin', component: AdminComponent },
  { path: 'reviewers', component: ReviewersComponent },
  { path: 'reviewers/details/:reviewer_id', component: ReviewersDetailsComponent },
  { path: 'sample-styles', component: SampleStylesComponent },
  { path: 'library', component: LibrariesComponent },
  { path: 'opener', component: TestOpenerComponent },
  ...(environment.isWorkspaceActivated
    ? ([
        {
          path: 'workspaces',
          component: WorkspaceListComponent,
          canActivate: [WorkspaceActivatedGuard],
          canActivateChild: [WorkspaceActivatedGuard],
          title: 'DRIFT - Workspace list',
          pathMatch: 'full',
          resolve: {
            metadata: MetadataTitleResolver
          },
          data: {
            meta: {
              description: 'List of existing workspace elements',
              keywords: 'List, Workspace, Pagination, Filtering',
              title: 'DRIFT - Workspace list'
            } satisfies RouteMetadata
          }
        },
        {
          path: 'workspaces/template',
          canActivate: [WorkspaceActivatedGuard],
          canActivateChild: [WorkspaceActivatedGuard],
          title: 'DRIFT - Workspace Templates',
          data: {
            meta: {
              description: 'Manage workspace templates',
              keywords: 'Templates, Workspace, Management',
              title: 'DRIFT - Workspace Templates'
            } satisfies RouteMetadata
          },
          children: [
            { path: '', component: TemplateListComponent, pathMatch: 'full' },
            { path: 'create', title: 'DRIFT - Create template', pathMatch: 'full', component: TemplateCreateComponent },
            {
              path: ':templateId',
              title: 'DRIFT - Edit template',
              pathMatch: 'full',
              component: TemplateEditComponent
            },
            { path: '*', redirectTo: 'create' }
          ]
        },
        {
          path: 'workspaces/create',
          component: WorkspaceTabComponent,
          canActivate: [WorkspaceActivatedGuard],
          canActivateChild: [WorkspaceActivatedGuard],
          resolve: {
            metadata: MetadataTitleResolver
          },
          title: 'DRIFT - Create workspace',
          data: {
            meta: {
              description: 'Create a new workspace',
              keywords: 'Create, Add, New, workspace',
              title: 'DRIFT - Create workspace'
            } satisfies RouteMetadata
          },
          children: [
            { path: 'common', pathMatch: 'full', data: { tabTitle: 'Common' }, component: CreateCommonFormComponent },
            { path: '', redirectTo: 'common', pathMatch: 'full' },
            { path: '*', redirectTo: 'common' }
          ]
        },
        {
          path: 'workspaces/:workspaceId',
          component: WorkspaceTabComponent,
          resolve: {
            metadata: MetadataTitleResolver
          },
          title: 'DRIFT - Edit workspace',
          data: {
            meta: {
              description: 'Edit existing workspace',
              keywords: 'Edit, Modify, Workspace',
              title: 'DRIFT - Edit workspace'
            } satisfies RouteMetadata,
            roles: ['admin', 'viewer', 'editor', 'reviewer'] satisfies WorkspaceRole[]
          },
          canActivate: [WorkspaceMemberGuard, WorkspaceActivatedGuard, WorkspaceRoleGuard, WorkspaceDeletedGuard],
          canActivateChild: [WorkspaceMemberGuard, WorkspaceActivatedGuard, WorkspaceRoleGuard, WorkspaceDeletedGuard],
          children: [
            { path: 'home', title: 'DRIFT - Workspace home', component: EditHomeComponent, data: { tabTitle: 'Home' } },
            {
              title: 'DRIFT - Workspace document editing',
              path: 'document-editing',
              component: EditDocumentComponent,
              data: { tabTitle: 'Document editing' }
            },
            {
              path: 'task',
              title: 'DRIFT - Workspace task',
              component: TaskViewComponent,
              data: { tabTitle: 'Tasks', roles: ['admin', 'viewer', 'editor', 'reviewer'] satisfies WorkspaceRole[] },
              children: [
                {
                  path: 'kanban',
                  component: TaskKanbanComponent,
                  data: { roles: ['admin', 'viewer', 'editor', 'reviewer'] satisfies WorkspaceRole[] },

                  children: [
                    {
                      path: ':taskId',
                      component: TaskDetailsDialogRouteComponent,
                      outlet: 'task-details'
                    }
                  ]
                },
                {
                  path: 'list',
                  component: TaskListComponent,
                  data: { roles: ['admin', 'viewer', 'editor', 'reviewer'] satisfies WorkspaceRole[] },

                  children: [
                    {
                      path: ':taskId',
                      component: TaskDetailsDialogRouteComponent,
                      outlet: 'task-details'
                    }
                  ]
                },
                {
                  path: 'timeline',
                  component: TaskTimelineComponent,
                  data: { roles: ['admin', 'viewer', 'editor', 'reviewer'] satisfies WorkspaceRole[] }
                },
                {
                  path: ':taskId',
                  component: TaskDetailsPageRouteComponent
                },
                { path: '', redirectTo: 'kanban', pathMatch: 'full' },
                { path: '*', redirectTo: 'list' }
              ]
            },
            {
              path: 'version',
              title: 'DRIFT - Versions',
              component: VersionHistoryComponent,
              data: { tabTitle: 'Version' }
            },
            { path: '', redirectTo: 'home', pathMatch: 'full' },
            { path: '*', redirectTo: 'home' }
          ]
        },
        {
          path: 'workspaces/:workspaceId/accept',
          component: WorkspaceAcceptComponent,
          resolve: {
            metadata: MetadataTitleResolver
          },
          title: 'DRIFT - Join workspace',
          data: {
            meta: {
              description: 'Join existing workspace',
              keywords: 'Join, Accept, Workspace, Invitation',
              title: 'DRIFT - Join workspace'
            } satisfies RouteMetadata
          }
        }
      ] satisfies Route[])
    : [])
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {}
